import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from '../../../../../constants/coefficients';
import {
	MAX_STAIR_HEIGHT,
	MIN_STAIR_WIDTH,
} from '../../../../../constants/limits';
import BlockType from '../../../../../enums/blockType';
import EdgeLabel from '../../../../../enums/edgeLabel';
import EdgeType from '../../../../../enums/edgeType';
import { Block, Edge } from '../../../../../types/shared/blocks';
import { getOppositeEdge } from './edgeUtils';
import { v4 as uuidv4 } from 'uuid';

function getDefaultStair(
	parentBlock: Block | null,
	selectedBlockEdge: Edge | undefined
) {
	if (!parentBlock || !selectedBlockEdge) return;

	const parentHeight = parentBlock?.dimensions?.height || 0;

	let addIntermediateLanding = false;
	let inferedHeight = parentHeight;

	if (parentHeight > MAX_STAIR_HEIGHT) {
		addIntermediateLanding = true;
		inferedHeight = Math.ceil(parentHeight / 2);
	}

	const widthDimension =
		selectedBlockEdge.label == EdgeLabel.TOP ||
		selectedBlockEdge.label == EdgeLabel.BOTTOM
			? 'width'
			: 'height';

	const inferedWidth =
		parentBlock?.type == BlockType.LANDING
			? widthDimension == 'width'
				? parentBlock.displayDimensions.width
				: parentBlock.displayDimensions.height
			: MIN_STAIR_WIDTH;

	const numberOfSteps = Math.ceil(inferedHeight / STEPS_COEFFICIENT);

	const depth = numberOfSteps * DEPTH_COEFFICIENT;

	const endHeight = parentHeight - inferedHeight;

	const stairId = uuidv4();

	const defaultStair: Block = {
		id: stairId,
		type: BlockType.STAIR,
		// Position will be set in the reducer
		dimensions: {
			width: inferedWidth,
			startHeight: parentHeight,
			height: inferedHeight,
			endHeight,
			numberOfSteps,
			depth,
		}, // Example dimensions
		displayDimensions: {
			width:
				selectedBlockEdge.label == EdgeLabel.TOP ||
				selectedBlockEdge.label == EdgeLabel.BOTTOM
					? inferedWidth
					: depth,
			height:
				selectedBlockEdge.label == EdgeLabel.TOP ||
				selectedBlockEdge.label == EdgeLabel.BOTTOM
					? depth
					: inferedWidth,
		},
		parentBlock: {
			id: parentBlock?.id,
			type: parentBlock?.type,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();
			const type =
				edge === selectedBlockEdge.label
					? endHeight > 0
						? EdgeType.RAILING
						: EdgeType.GROUND
					: edge === getOppositeEdge(selectedBlockEdge.label)
						? EdgeType.BLOCK_ATTACHED
						: EdgeType.RAILING;
			const length =
				edge === selectedBlockEdge.label ||
				edge === getOppositeEdge(selectedBlockEdge.label)
					? inferedWidth
					: depth;

			return {
				id: edgeId,
				label: edge,
				attachedToParent: edge === getOppositeEdge(selectedBlockEdge.label),
				type,
				length,
				segments: [
					{
						id: uuidv4(),
						edgeId: edgeId,
						blockId: stairId,
						length,
						type,
					},
				],
			};
		}),
	};

	return { defaultStair, addIntermediateLanding };
}

export function getDefaultStairV2({
	parentBlockId,
	parentBlockType,
	startHeight,
	blockEdge,
	parentBlockDisplayWidth,
	parentBlockDisplayHeight,
}: {
	parentBlockId: string | undefined;
	parentBlockType: BlockType;
	startHeight: number | undefined;
	blockEdge: Edge | undefined;
	parentBlockDisplayWidth: number;
	parentBlockDisplayHeight: number;
}) {
	if (!parentBlockId || !blockEdge || !startHeight) return;

	const widthDimension =
		blockEdge.label == EdgeLabel.TOP || blockEdge.label == EdgeLabel.BOTTOM
			? 'width'
			: 'height';

	const inferedWidth =
		parentBlockType == BlockType.LANDING
			? widthDimension == 'width'
				? parentBlockDisplayWidth
				: parentBlockDisplayHeight
			: MIN_STAIR_WIDTH;

	const numberOfSteps = Math.ceil(startHeight / STEPS_COEFFICIENT);

	const depth = numberOfSteps * DEPTH_COEFFICIENT;

	const endHeight = 0;

	const stairId = uuidv4();

	const defaultStair: Block = {
		id: stairId,
		type: BlockType.STAIR,
		// Position will be set in the reducer
		dimensions: {
			width: inferedWidth,
			startHeight: startHeight,
			height: startHeight,
			endHeight,
			numberOfSteps,
			depth,
		}, // Example dimensions
		displayDimensions: {
			width:
				blockEdge.label == EdgeLabel.TOP || blockEdge.label == EdgeLabel.BOTTOM
					? inferedWidth
					: depth,
			height:
				blockEdge.label == EdgeLabel.TOP || blockEdge.label == EdgeLabel.BOTTOM
					? depth
					: inferedWidth,
		},
		parentBlock: {
			id: parentBlockId,
			type: parentBlockType,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();
			const type =
				edge === blockEdge.label
					? endHeight > 0
						? EdgeType.RAILING
						: EdgeType.GROUND
					: edge === getOppositeEdge(blockEdge.label)
						? EdgeType.BLOCK_ATTACHED
						: EdgeType.RAILING;
			const length =
				edge === blockEdge.label || edge === getOppositeEdge(blockEdge.label)
					? inferedWidth
					: depth;

			return {
				id: edgeId,
				label: edge,
				attachedToParent: edge === getOppositeEdge(blockEdge.label),
				type,
				length,
				segments: [
					{
						id: uuidv4(),
						edgeId: edgeId,
						blockId: stairId,
						length,
						type,
					},
				],
			};
		}),
	};

	return defaultStair;
}

export default getDefaultStair;
